import Link from 'shared/timber/v1/Link'

import { TaskResponsibilityEnumType } from '~/common/types/graphql'
import { CardHeader } from '~/views/components/elements/overview'
import Text from '~/views/components/ui/Text'

import { StaticCard, StaticCardBody } from './Styled'
import TaskBreakdown from './TaskBreakdownV2'
import TaskHeroNumber, {
  Props as TaskHeroNumberProps,
} from './TaskHeroNumberV2'
import { useStaticLabels } from 'shared/hooks/use-static-labels'

export interface Props {
  className?: string
  project?: TaskHeroNumberProps['project'] & {
    id: string
    firstActiveTemplate?: string | null
  }
  responsibility: TaskResponsibilityEnumType
  title?: string | null
  stats?: {
    done?: number
    notStarted?: number
    overdue?: number
    planned?: number
    remaining?: number
    signOff?: number
    working?: number
    stuck?: number
  } | null
  hours?: null | {
    actual?: number
    estimated?: number
    remaining?: number
  }
  isProvider?: boolean
}

const TaskSummaryCard = ({
  className,
  title = '',
  project,
  responsibility,
  stats,
  hours,
  isProvider = false,
}: Props) => {
  const [getCommonLabel] = useStaticLabels(['common'])

  const ResponsibilityChipMap = {
    [TaskResponsibilityEnumType.Internal]: getCommonLabel('internal'),
    [TaskResponsibilityEnumType.Customer]: getCommonLabel('customer'),
    [TaskResponsibilityEnumType.ThirdParty]: getCommonLabel('thirdParty'),
    [TaskResponsibilityEnumType.All]: getCommonLabel('all'),
    [TaskResponsibilityEnumType.InternalInProject]:
      getCommonLabel('internalInProject'),
    [TaskResponsibilityEnumType.NotInProject]: getCommonLabel('notInProject'),
  }

  const taskFilters = { responsibilities: [responsibility] }
  const displayValue = ResponsibilityChipMap[responsibility]

  const filters = [
    {
      chipName: getCommonLabel('responsibility'),
      displayValue: displayValue,
      name: 'responsibilities',
      values: [responsibility],
    },
  ]

  return (
    <StaticCard className={className} data-testid={'task-summary-card'}>
      <Link
        href={{
          pathname: `/app/projects/${project?.id}/plan`,
          query: {
            'template-id': project?.firstActiveTemplate,
            'task-filters': JSON.stringify(taskFilters),
            filters: JSON.stringify(filters),
          },
        }}
      >
        <CardHeader data-testid={'card-header'}>
          <Text weight="bold" size="large">
            {title}
          </Text>
        </CardHeader>
      </Link>
      <StaticCardBody data-testid={'card-body'}>
        <TaskHeroNumber
          project={project}
          tasks={{
            overdue: stats?.overdue,
            remaining: stats?.remaining,
            done: stats?.done,
            planned: stats?.planned,
            notStarted: stats?.notStarted,
            signOff: stats?.signOff,
            stuck: stats?.stuck,
            working: stats?.working,
          }}
          hours={hours}
          isProvider={isProvider}
        />

        <TaskBreakdown
          notStarted={stats?.notStarted}
          done={stats?.done}
          working={stats?.working}
          stuck={stats?.stuck}
          signOff={stats?.signOff}
          labels={{
            notStarted: getCommonLabel('notStarted'),
            inProgress: getCommonLabel('inProgress'),
            stuck: getCommonLabel('stuck'),
            signOff: getCommonLabel('signOff'),
            done: getCommonLabel('done'),
          }}
        />
      </StaticCardBody>
    </StaticCard>
  )
}

export default TaskSummaryCard
